import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Car, CheckCircle2, RotateCcw, RefreshCw, Reply, Receipt } from "lucide-react";
import QuoteDialog, { QuoteSummary } from "./AdminQuote";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOPIC = { quote: ["Quote", "border-brand/40 bg-brand/10 text-brand"], question: ["Question", "border-sky-400/40 bg-sky-400/10 text-sky-300"], other: ["Other", "border-white/15 bg-white/5 text-white/60"] };
const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });

const AdminQuestions = ({ token, onUnauthorized, onCounts }) => {
  const [tab, setTab] = useState("open");
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [quoteTarget, setQuoteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/questions`, { params: tab === "all" ? {} : { status: tab }, headers: { Authorization: `Bearer ${token}` } });
      setData(d);
      onCounts?.(d.counts);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
    } finally { setLoading(false); }
  }, [tab, token, onUnauthorized, onCounts]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (q, status) => {
    setBusy(q.id);
    try {
      await axios.post(`${API}/admin/questions/${q.id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(status === "answered" ? "Marked as answered" : "Reopened");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-questions">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-bold text-white">Questions & Quotes</h1>
          <p className="mt-1 text-white/55">Messages from the website. Reply by email or phone, or send a quote or invoice they can accept, book and pay online.</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2 text-sm text-white/80 hover:border-white/40" data-testid="questions-refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />Refresh</button>
      </div>
      <div className="mt-8 grid grid-cols-2 gap-4 sm:max-w-md">
        {[["open", "Open", "text-amber-300"], ["answered", "Answered", "text-emerald-300"]].map(([k, l, c]) => (
          <div key={k} className="rounded-xl border border-white/10 bg-[#111217] p-5">
            <div className="text-[12px] uppercase tracking-[0.1em] text-white/45">{l}</div>
            <div className={`mt-1 font-display text-3xl font-bold ${c}`} data-testid={`q-count-${k}`}>{data.counts?.[k] ?? 0}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 flex flex-wrap gap-2">
        {[["open", "Open"], ["answered", "Answered"], ["all", "All"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors ${tab === k ? "border-brand bg-brand text-white" : "border-white/15 text-white/70 hover:text-white"}`} data-testid={`q-tab-${k}`}>{l}</button>
        ))}
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        {data.items.map((q) => {
          const [tLabel, tCls] = TOPIC[q.topic] || TOPIC.other;
          const car = [q.make, q.model, q.year].filter(Boolean).join(" ");
          return (
            <div key={q.id} className="flex flex-col rounded-2xl border border-white/10 bg-[#111217] p-5 transition-colors hover:border-white/20" data-testid={`question-${q.reference}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display text-xl font-bold text-white">{q.name}</span>
                    <span className={`rounded-md border px-2 py-0.5 text-[11px] font-bold uppercase ${tCls}`}>{tLabel}</span>
                    {q.status === "answered" && <span className="rounded-md border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[11px] font-bold uppercase text-emerald-300">Answered</span>}
                  </div>
                  <div className="mt-0.5 text-[13px] text-white/45">{q.reference} · {fmt(q.created_at)}</div>
                </div>
              </div>
              <p className="mt-4 flex-1 whitespace-pre-line rounded-lg bg-white/[0.03] p-3 text-[15px] leading-relaxed text-white/80">{q.message}</p>
              <div className="mt-4 grid gap-2 text-sm text-white/70 sm:grid-cols-2">
                <a href={`mailto:${q.email}`} className="flex items-center gap-2 hover:text-brand"><Mail className="h-4 w-4 text-white/40" />{q.email}</a>
                {q.phone && <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-brand"><Phone className="h-4 w-4 text-white/40" />{q.phone}</a>}
                {car && <span className="flex items-center gap-2"><Car className="h-4 w-4 text-white/40" />{car}</span>}
              </div>
              {q.quote && <QuoteSummary q={q.quote} token={token} onChanged={load} />}
              <div className="mt-4 flex flex-wrap gap-2 border-t border-white/10 pt-4">
                {!q.quote?.booking && !q.quote?.balance_paid && <button onClick={() => setQuoteTarget(q)} className="flex items-center gap-1.5 rounded-lg border border-brand/50 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand/15" data-testid={`q-quote-${q.reference}`}><Receipt className="h-4 w-4" />{q.quote ? "New quote / invoice" : "Send quote / invoice"}</button>}
                <a href={`mailto:${q.email}?subject=${encodeURIComponent(`Re: your EvtTuning ${tLabel.toLowerCase()} (${q.reference})`)}`} className="flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#d0141c]" data-testid={`reply-${q.reference}`}><Reply className="h-4 w-4" />Reply by email</a>
                {q.status === "open" ? (
                  <button disabled={busy === q.id} onClick={() => setStatus(q, "answered")} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 transition-colors hover:border-emerald-400 hover:text-emerald-300 disabled:opacity-60" data-testid={`answered-${q.reference}`}><CheckCircle2 className="h-4 w-4" />Mark answered</button>
                ) : (
                  <button disabled={busy === q.id} onClick={() => setStatus(q, "open")} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 transition-colors hover:border-white/40 disabled:opacity-60" data-testid={`reopen-${q.reference}`}><RotateCcw className="h-4 w-4" />Reopen</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <QuoteDialog booking={quoteTarget} kind="question" token={token} onClose={() => setQuoteTarget(null)} onSent={() => { setQuoteTarget(null); if (tab === "open") toast.info("Moved to Answered"); load(); }} />
      {!loading && data.items.length === 0 && <div className="mt-6 rounded-2xl border border-dashed border-white/10 p-12 text-center text-white/45" data-testid="questions-empty">No {tab === "all" ? "" : tab} questions.</div>}
    </div>
  );
};

export default AdminQuestions;
